import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useAppDispatch, useUser, DamageRequestFilters } from '../../hooks'
import {
    APARTMENT,
    axiosInstance,
    currencyFormatter,
    damageCostDeltaForStatusChange,
    VENDOR,
} from '../../helpers'
import {
    Damage,
    DamageConfig,
    DamageStatus,
    DamageStatusCounts,
    DamageStatusList,
    getDamageStatusName,
    IdentifiableObject,
} from '../../models'
import {
    getDamageConfigList,
    getInfrastructure,
    RootState,
    setWorkSpaceUser,
} from '../../store'
import { Routes } from '../../navigation/config/routes'

import {
    Box,
    ButtonBase,
    Chip,
    CircularProgress,
    Paper,
    Tab,
    Tooltip,
    Tabs,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { toast } from 'react-toastify'

import { WorkSpaceUser } from '../../models'

import { Container, SideDrawerContainer } from '../../components'
import { DamageConfigForm } from './DamageConfigForm'

import { DamageConfigContainer } from './DamageConfig'
import { FORM_CLOSED } from '../InfrastructureConfig'
import {
    DamageList,
    defaultDamageFilter,
    DamageDateStatusFilterApply,
    DamageDateStatusFilterSnapshot,
} from './DamageList'
import {
    PropertyDamageBreakdownModal,
    PropertyDamageSummaryRow,
} from './PropertyDamageBreakdownModal'

export const DAMAGE_FORM_CLOSED = 0
export const DAMAGE_LIST = 1
export const DAMAGE_CONFIG = 2
export const MOVE_INSPECTIONS = 3

type DamageFormSelection =
    | typeof DAMAGE_LIST
    | typeof DAMAGE_CONFIG
    | typeof MOVE_INSPECTIONS

type DamageFormOpenState = typeof DAMAGE_FORM_CLOSED | DamageFormSelection

export interface DamageDrawerState {
    open: DamageFormOpenState
    selected?: IdentifiableObject
}

export const closedDamageDrawer: DamageDrawerState = {
    open: DAMAGE_FORM_CLOSED,
}

const BRAND_TEAL = '#008C85'

function statusAccentColor(status: DamageStatus): string {
    switch (status) {
        case DamageStatus.DENIED:
        case DamageStatus.SYNC_FAILED:
            return '#c62828'
        case DamageStatus.PRE_APPROVED:
            return '#2e7d5e'
        case DamageStatus.APPROVED:
            return '#2e7d32'
        case DamageStatus.PENDING:
            return '#f9a825'
        case DamageStatus.SYNC_PENDING:
        case DamageStatus.ENTRATA_SYNCED:
            return '#1565c0'
        default:
            return BRAND_TEAL
    }
}

function parseStatusCountsPayload(
    data: Record<string, unknown>,
): DamageStatusCounts {
    return {
        [DamageStatus.PENDING]: Number(data[DamageStatus.PENDING]) || 0,
        [DamageStatus.PRE_APPROVED]:
            Number(data[DamageStatus.PRE_APPROVED]) || 0,
        [DamageStatus.APPROVED]: Number(data[DamageStatus.APPROVED]) || 0,
        [DamageStatus.SYNC_PENDING]:
            Number(data[DamageStatus.SYNC_PENDING]) || 0,
        [DamageStatus.ENTRATA_SYNCED]:
            Number(data[DamageStatus.ENTRATA_SYNCED]) || 0,
        [DamageStatus.DENIED]: Number(data[DamageStatus.DENIED]) || 0,
        [DamageStatus.SYNC_FAILED]: Number(data[DamageStatus.SYNC_FAILED]) || 0,
    }
}

export const DamageHome = () => {
    const [drawerState, setDrawerState] = useState<DamageDrawerState>(
        closedDamageDrawer,
    )
    const [tab, setTab] = useState<DamageFormSelection>(DAMAGE_LIST)

    const dispatch = useAppDispatch()
    const theme = useTheme()
    const location = useLocation()
    const { workspaceUser, rootUser, organization } = useUser()

    const [portfolioLoading, setPortfolioLoading] = useState(false)
    const [portfolioTotalCost, setPortfolioTotalCost] = useState<number | null>(
        null,
    )
    const [
        portfolioStatusCounts,
        setPortfolioStatusCounts,
    ] = useState<DamageStatusCounts | null>(null)
    const [portfolioListFilterParams, setPortfolioListFilterParams] = useState<
        DamageRequestFilters | undefined
    >(undefined)

    const [propertyBreakdownOpen, setPropertyBreakdownOpen] = useState(false)
    const [propertyBreakdownLoading, setPropertyBreakdownLoading] = useState(
        false,
    )
    const [propertyBreakdownRows, setPropertyBreakdownRows] = useState<
        PropertyDamageSummaryRow[]
    >([])
    const [
        dateStatusFilter,
        setDateStatusFilter,
    ] = useState<DamageDateStatusFilterSnapshot>({
        created_after: defaultDamageFilter.created_after,
        created_before: defaultDamageFilter.created_before,
        status: defaultDamageFilter.status,
    })
    const dateStatusFilterApplyRef = useRef<DamageDateStatusFilterApply | null>(
        null,
    )

    const handleDateStatusFilterSnapshot = useCallback(
        (snapshot: DamageDateStatusFilterSnapshot) => {
            setDateStatusFilter(snapshot)
        },
        [],
    )
    const isEzfolioDamagesPage =
        location.pathname.replace(/\/+$/, '') ===
        Routes.organization.damages.replace(/\/+$/, '')

    const workspaceOptions = useMemo(() => {
        if (!rootUser?.workspaces) return []
        return rootUser.workspaces.filter((ws) => {
            const companyType = ws.active_workspace?.company_type
            if (companyType === VENDOR) return false
            if (companyType !== APARTMENT) return false
            if (organization != null) {
                return ws.active_workspace.organization === organization
            }
            return true
        })
    }, [rootUser?.workspaces, organization])

    const damageConfigList: DamageConfig[] | undefined = useSelector(
        (state: RootState) => state.aptConfig.damageConfigList,
    )

    useEffect(() => {
        dispatch(getDamageConfigList({}))
        setDrawerState(closedDamageDrawer)
    }, [tab, workspaceUser?.active_workspace?.id])

    const selectedPropertyOption: WorkSpaceUser | undefined = useMemo(() => {
        if (!workspaceUser?.active_workspace) return undefined
        return (
            workspaceOptions.find(
                (ws) =>
                    ws.active_workspace.id ===
                    workspaceUser.active_workspace.id,
            ) ?? undefined
        )
    }, [workspaceOptions, workspaceUser?.active_workspace])

    const portfolioRequestParams = useMemo(() => {
        if (!isEzfolioDamagesPage || organization == null) return null
        return {
            ...(portfolioListFilterParams ?? { excludeDenied: true }),
            organization,
        }
    }, [isEzfolioDamagesPage, organization, portfolioListFilterParams])

    useEffect(() => {
        if (portfolioRequestParams == null) {
            setPortfolioTotalCost(null)
            setPortfolioStatusCounts(null)
            return
        }

        let cancelled = false
        setPortfolioLoading(true)
        const params = portfolioRequestParams

        Promise.all([
            axiosInstance.get('lease/damage/get_total_cost/', { params }),
            axiosInstance.get('lease/damage/get_status_counts/', { params }),
        ])
            .then(([costRes, countsRes]) => {
                if (cancelled) return
                const raw = costRes.data
                const costNum =
                    typeof raw === 'number'
                        ? raw
                        : typeof raw === 'string'
                        ? parseFloat(raw)
                        : Number(raw)
                setPortfolioTotalCost(Number.isFinite(costNum) ? costNum : 0)
                setPortfolioStatusCounts(
                    parseStatusCountsPayload(
                        countsRes.data as Record<string, unknown>,
                    ),
                )
            })
            .catch(() => {
                if (!cancelled) {
                    toast.error('Could not load portfolio summary')
                }
            })
            .finally(() => {
                if (!cancelled) setPortfolioLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [portfolioRequestParams])

    const closePropertyBreakdown = useCallback(() => {
        setPropertyBreakdownOpen(false)
    }, [])

    const openPropertyBreakdown = useCallback(() => {
        if (portfolioRequestParams == null) return
        setPropertyBreakdownOpen(true)
    }, [portfolioRequestParams])

    useEffect(() => {
        if (!propertyBreakdownOpen || portfolioRequestParams == null) {
            return
        }

        let cancelled = false
        setPropertyBreakdownLoading(true)
        setPropertyBreakdownRows([])
        const params = portfolioRequestParams

        axiosInstance
            .get('lease/damage/properties-damage-summary/', { params })
            .then((res) => {
                if (cancelled) return
                const raw = res.data?.properties
                const rows: PropertyDamageSummaryRow[] = Array.isArray(raw)
                    ? raw.map((row: Record<string, unknown>) => ({
                          apartment_id: Number(row.apartment_id) || 0,
                          name: String(row.name ?? ''),
                          damage_count: Number(row.damage_count) || 0,
                          total_cost: Number(row.total_cost) || 0,
                      }))
                    : []
                setPropertyBreakdownRows(rows)
            })
            .catch(() => {
                if (!cancelled) {
                    toast.error('Could not load property breakdown')
                    closePropertyBreakdown()
                }
            })
            .finally(() => {
                if (!cancelled) setPropertyBreakdownLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [propertyBreakdownOpen, portfolioRequestParams, closePropertyBreakdown])

    const handleBreakdownDateChange = useCallback(
        (startDate: Date, endDate: Date) => {
            dateStatusFilterApplyRef.current?.(
                startDate,
                endDate,
                dateStatusFilter.status,
            )
        },
        [dateStatusFilter.status],
    )

    const handleBreakdownStatusChange = useCallback(
        (statuses: DamageStatus[]) => {
            dateStatusFilterApplyRef.current?.(
                dateStatusFilter.created_after,
                dateStatusFilter.created_before,
                statuses,
            )
        },
        [dateStatusFilter.created_after, dateStatusFilter.created_before],
    )

    const applyPortfolioTotalsAfterDamageStatusChange = useCallback(
        (damages: Damage[], newStatus: DamageStatus) => {
            if (!isEzfolioDamagesPage || organization == null) return
            const params = portfolioListFilterParams ?? { excludeDenied: true }
            const delta = damageCostDeltaForStatusChange(
                damages,
                newStatus,
                params,
            )
            setPortfolioTotalCost((prev) =>
                prev != null && Number.isFinite(prev) ? prev + delta : prev,
            )
            setPortfolioStatusCounts((prev) => {
                if (!prev) return prev
                const next = { ...prev }
                damages.forEach((d) => {
                    next[d.status] = Math.max(0, (next[d.status] ?? 0) - 1)
                    next[newStatus] = (next[newStatus] ?? 0) + 1
                })
                return next
            })
        },
        [isEzfolioDamagesPage, organization, portfolioListFilterParams],
    )

    const handleEzfolioPropertyChange = (
        _event: React.ChangeEvent<{}>,
        newValue: WorkSpaceUser | null,
    ) => {
        if (newValue) {
            dispatch(setWorkSpaceUser(newValue))
            toast.success(`Switched to ${newValue.active_workspace.name}`)
        }
    }

    return (
        <SideDrawerContainer open={drawerState.open !== FORM_CLOSED}>
            <Container
                direction="column"
                flex={1}
                style={{
                    height: 'calc(100vh - 104px)',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                }}
            >
                {isEzfolioDamagesPage && workspaceUser?.active_workspace && (
                    <Container
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        style={{
                            gap: theme.spacing(2),
                            padding: theme.spacing(1.5, 2, 1),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            flexWrap="wrap"
                            style={{ minWidth: 0 }}
                        >
                            {workspaceOptions.length > 1 ? (
                                <Autocomplete
                                    disableClearable
                                    id="ezfolio-damages-property"
                                    options={workspaceOptions}
                                    getOptionLabel={(option) =>
                                        option.active_workspace?.name ?? ''
                                    }
                                    value={selectedPropertyOption}
                                    onChange={handleEzfolioPropertyChange}
                                    style={{ minWidth: 300, maxWidth: 420 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Property"
                                            variant="outlined"
                                            margin="dense"
                                            size="small"
                                            placeholder="Search"
                                        />
                                    )}
                                />
                            ) : (
                                <TextField
                                    label="Property"
                                    value={workspaceUser.active_workspace.name}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                    style={{ minWidth: 300, maxWidth: 420 }}
                                />
                            )}
                        </Box>
                        {organization != null && (
                            <Paper
                                elevation={0}
                                style={{
                                    borderRadius: 8,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderLeft: `3px solid ${BRAND_TEAL}`,
                                    backgroundColor: '#fff',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                                    padding: theme.spacing(1.25, 1.75),
                                    flexShrink: 0,
                                    maxWidth: '100%',
                                }}
                            >
                                {portfolioLoading ? (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        style={{
                                            gap: theme.spacing(1.5),
                                            minHeight: 40,
                                        }}
                                    >
                                        <CircularProgress
                                            size={20}
                                            thickness={4}
                                            style={{ color: BRAND_TEAL }}
                                        />
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            Loading portfolio…
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        alignItems="center"
                                        style={{
                                            gap: theme.spacing(1.5),
                                            rowGap: 10,
                                        }}
                                    >
                                        <Typography
                                            variant="overline"
                                            style={{
                                                color: BRAND_TEAL,
                                                fontWeight: 700,
                                                letterSpacing: '0.12em',
                                                fontSize: 10,
                                                lineHeight: 1,
                                                marginRight: theme.spacing(0.5),
                                            }}
                                        >
                                            Portfolio
                                        </Typography>

                                        <Tooltip title="Breakdown by property">
                                            <span>
                                                <ButtonBase
                                                    focusRipple
                                                    disabled={portfolioLoading}
                                                    onClick={
                                                        openPropertyBreakdown
                                                    }
                                                    aria-label="View damage breakdown by property"
                                                    style={{
                                                        borderRadius: 8,
                                                        textAlign: 'left',
                                                        display: 'block',
                                                    }}
                                                >
                                                    <Box
                                                        display="flex"
                                                        alignItems="stretch"
                                                        style={{
                                                            borderRadius: 8,
                                                            border: `1px solid ${theme.palette.divider}`,
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[50],
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <Box
                                                            px={1.5}
                                                            py={0.75}
                                                            display="flex"
                                                            flexDirection="column"
                                                            justifyContent="center"
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                style={{
                                                                    fontSize: 10,
                                                                    lineHeight: 1.2,
                                                                    display:
                                                                        'block',
                                                                }}
                                                            >
                                                                Properties
                                                            </Typography>
                                                            <Typography
                                                                variant="subtitle1"
                                                                component="span"
                                                                style={{
                                                                    fontWeight: 700,
                                                                    lineHeight: 1.3,
                                                                }}
                                                            >
                                                                {
                                                                    workspaceOptions.length
                                                                }
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            style={{
                                                                width: 1,
                                                                alignSelf:
                                                                    'stretch',
                                                                backgroundColor:
                                                                    theme
                                                                        .palette
                                                                        .divider,
                                                            }}
                                                        />
                                                        <Box
                                                            px={1.5}
                                                            py={0.75}
                                                            display="flex"
                                                            flexDirection="column"
                                                            justifyContent="center"
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                style={{
                                                                    fontSize: 10,
                                                                    lineHeight: 1.2,
                                                                    display:
                                                                        'block',
                                                                }}
                                                            >
                                                                Total cost
                                                            </Typography>
                                                            <Typography
                                                                variant="subtitle1"
                                                                component="span"
                                                                style={{
                                                                    fontWeight: 700,
                                                                    lineHeight: 1.3,
                                                                    color: BRAND_TEAL,
                                                                }}
                                                            >
                                                                {currencyFormatter.format(
                                                                    portfolioTotalCost ??
                                                                        0,
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </ButtonBase>
                                            </span>
                                        </Tooltip>

                                        <Box
                                            display="flex"
                                            flexWrap="wrap"
                                            alignItems="center"
                                            style={{
                                                gap: 6,
                                                flex: 1,
                                                minWidth: 200,
                                            }}
                                        >
                                            {portfolioStatusCounts &&
                                                DamageStatusList.map(
                                                    (status) => {
                                                        const accent = statusAccentColor(
                                                            status,
                                                        )
                                                        return (
                                                            <Chip
                                                                key={status}
                                                                size="small"
                                                                label={`${getDamageStatusName(
                                                                    status,
                                                                )} · ${
                                                                    portfolioStatusCounts[
                                                                        status
                                                                    ]
                                                                }`}
                                                                style={{
                                                                    height: 26,
                                                                    fontSize: 11,
                                                                    fontWeight: 600,
                                                                    border:
                                                                        'none',
                                                                    borderLeft: `3px solid ${accent}`,
                                                                    backgroundColor: `${accent}18`,
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .text
                                                                            .primary,
                                                                }}
                                                            />
                                                        )
                                                    },
                                                )}
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        )}
                    </Container>
                )}
                <Tabs
                    style={{
                        backgroundColor: theme.palette.grey[200],
                        margin: theme.spacing(1),
                    }}
                    value={tab}
                    onChange={(_, v) => {
                        setTab(v)
                    }}
                >
                    <Tab label="Damage List" value={DAMAGE_LIST} />
                    <Tab label="Damage types" value={DAMAGE_CONFIG} />
                </Tabs>

                {tab === DAMAGE_LIST && (
                    <DamageList
                        damageConfigList={damageConfigList}
                        onListFilterParamsChange={
                            isEzfolioDamagesPage
                                ? setPortfolioListFilterParams
                                : undefined
                        }
                        onDamageStatusAdjusted={
                            isEzfolioDamagesPage
                                ? applyPortfolioTotalsAfterDamageStatusChange
                                : undefined
                        }
                        onDateStatusFilterSnapshot={
                            isEzfolioDamagesPage
                                ? handleDateStatusFilterSnapshot
                                : undefined
                        }
                        dateStatusFilterApplyRef={
                            isEzfolioDamagesPage
                                ? dateStatusFilterApplyRef
                                : undefined
                        }
                    />
                )}

                {tab === DAMAGE_CONFIG && (
                    <DamageConfigContainer
                        damageConfigList={damageConfigList}
                        drawerState={drawerState}
                        onSelectDamage={(damageConfig) => {
                            setDrawerState({
                                open: DAMAGE_CONFIG,
                                selected: damageConfig,
                            })
                        }}
                    />
                )}
            </Container>

            <DamageConfigForm
                open={drawerState.open !== DAMAGE_FORM_CLOSED}
                handleClose={() => setDrawerState(closedDamageDrawer)}
                damageConfig={drawerState.selected as DamageConfig | undefined}
            />

            {isEzfolioDamagesPage && (
                <PropertyDamageBreakdownModal
                    open={propertyBreakdownOpen}
                    loading={propertyBreakdownLoading}
                    rows={propertyBreakdownRows}
                    createdAfter={dateStatusFilter.created_after}
                    createdBefore={dateStatusFilter.created_before}
                    selectedStatuses={dateStatusFilter.status}
                    statusCounts={portfolioStatusCounts}
                    onDateChange={handleBreakdownDateChange}
                    onStatusChange={handleBreakdownStatusChange}
                    onClose={closePropertyBreakdown}
                />
            )}
        </SideDrawerContainer>
    )
}
